import { useCallback, useEffect, useRef, useState } from 'react'

const STORAGE_KEY = 'vibe-agent-panel-size'
const DEFAULT_WIDTH = 550
const MIN_WIDTH = 450
const MAX_WIDTH = 1000

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function loadWidth(): number {
  if (typeof window === 'undefined') return DEFAULT_WIDTH
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const { width } = JSON.parse(stored)
      return clamp(Number(width) || DEFAULT_WIDTH, MIN_WIDTH, MAX_WIDTH)
    }
  } catch {
    // ignore
  }
  return DEFAULT_WIDTH
}

export function useResizablePanel() {
  const [width, setWidth] = useState(loadWidth)
  const [isResizing, setIsResizing] = useState(false)

  const originXRef = useRef(0)
  const startWidthRef = useRef(DEFAULT_WIDTH)

  const startResize = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()
      originXRef.current = e.clientX
      startWidthRef.current = width
      setIsResizing(true)
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    },
    [width],
  )

  const resizeBy = useCallback((delta: number) => {
    setWidth((current) => clamp(current + delta, MIN_WIDTH, MAX_WIDTH))
  }, [])

  const resizeTo = useCallback((nextWidth: number) => {
    setWidth(clamp(nextWidth, MIN_WIDTH, MAX_WIDTH))
  }, [])

  useEffect(() => {
    if (!isResizing) return

    const onMove = (e: PointerEvent) => {
      // Left-edge handle: drag left = wider (origin - current)
      const dx = originXRef.current - e.clientX
      setWidth(clamp(startWidthRef.current + dx, MIN_WIDTH, MAX_WIDTH))
    }

    const onUp = () => setIsResizing(false)

    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
    return () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
    }
  }, [isResizing])

  useEffect(() => {
    if (isResizing) return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ width }))
    } catch {
      // ignore (private mode, quota, disabled storage)
    }
  }, [width, isResizing])

  return {
    width,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    isResizing,
    startResize,
    resizeBy,
    resizeTo,
  }
}
